import { FC, PropsWithChildren, ReactNode } from "react";

import Stack from "@mui/material/Stack";

import { useTranslate } from "@/common/hooks/useTranslate";
import { useSuppressChatWidget } from "@/common/hooks/useChatWidgetSuppression";
import { useSuppressZeffyLauncher } from "@/common/hooks/useZeffyLauncherSuppression";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDrawer } from "@/dashboard-design-system/Drawer";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Cross } from "@/icons/outlined";

interface TableFiltersMobileDrawerProps {
  open: boolean;
  onClose?: () => void;
  footer?: ReactNode;
}

export const TableFiltersMobileDrawer: FC<PropsWithChildren<TableFiltersMobileDrawerProps>> = ({
  open,
  onClose,
  footer,
  children,
}) => {
  const { t } = useTranslate();
  useSuppressChatWidget(open);
  useSuppressZeffyLauncher(open);

  return (
    <DashboardDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      noPadding
      noDivider
      scrollableContent
      data-test="payment-filters-mobile-drawer"
      height="90vh"
      sx={{
        "&& .MuiDrawer-paper": {
          "@supports (height: 100dvh)": { height: "90dvh" },
          overflow: "hidden",
        },
      }}
      slots={{
        header: (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            paddingTop={3}
            paddingBottom={1.5}
            paddingLeft={3}
            paddingRight={2}
          >
            <DashboardTypography variant="h5" vibe="text-neutral-intense" sx={{ flex: 1 }}>
              {t("dashboard", "paymentsPage.table.filters.title")}
            </DashboardTypography>
            <DashboardIconButton
              size="small"
              vibe="neutral"
              onClick={onClose}
              aria-label={t("dashboard", "common.close")}
              data-test="drawer-close-button"
              sx={(theme) => ({ width: theme.spacing(4.5), height: theme.spacing(4.5) })}
            >
              <Cross />
            </DashboardIconButton>
          </Stack>
        ),
        footer,
      }}
    >
      <Stack
        data-test="payment-filters-mobile-scroll-container"
        sx={(theme) => ({
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          scrollPaddingBlock: theme.spacing(2),
          "& > *": { flexShrink: 0 },
        })}
      >
        {children}
      </Stack>
    </DashboardDrawer>
  );
};
